import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X } from 'lucide-react';

const Header4 = ({ cartCount = 0 }) => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [mobileMenuOpen]);

    const navLinks = [
        { label: 'Home', path: '/template4' },
        { label: 'Shop', path: '/template4/shop' },
        { label: 'About', path: '/template4/about' },
        { label: 'Contact', path: '/template4/contact' },
        { label: 'Orders', path: '/template4/orders' },
    ];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <link href="https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

            <header
                className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
                style={{
                    backgroundColor: scrolled ? '#FFFFFF' : 'transparent',
                    borderBottom: scrolled ? '1px solid #E5E7EB' : '1px solid transparent',
                    boxShadow: scrolled ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16 md:h-20">
                    {/* Logo */}
                    <h1
                        onClick={() => navigate('/template4')}
                        className="text-xl md:text-2xl font-bold tracking-wider cursor-pointer"
                        style={{
                            fontFamily: "'Work Sans', sans-serif",
                            color: scrolled ? '#1B4965' : '#FFFFFF',
                        }}
                    >
                        NEELA
                    </h1>

                    {/* Desktop Nav */}
                    <nav className="hidden md:flex items-center gap-8">
                        {navLinks.map((link) => (
                            <button
                                key={link.label}
                                onClick={() => handleNavClick(link.path)}
                                className="text-sm font-medium tracking-wide uppercase transition-colors duration-300 bg-transparent border-none cursor-pointer relative group"
                                style={{
                                    fontFamily: "'Work Sans', sans-serif",
                                    color: scrolled ? '#1A1A1A' : '#FFFFFF',
                                    padding: 0,
                                }}
                            >
                                {link.label}
                                <span
                                    className="absolute bottom-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300"
                                    style={{ backgroundColor: scrolled ? '#1B4965' : '#62B6CB' }}
                                />
                            </button>
                        ))}
                    </nav>

                    {/* Right Icons */}
                    <div className="flex items-center gap-4">
                        <Search
                            size={20}
                            className="cursor-pointer transition-colors duration-300 hidden sm:block"
                            style={{ color: scrolled ? '#1A1A1A' : '#FFFFFF' }}
                        />
                        <User
                            size={20}
                            className="cursor-pointer transition-colors duration-300 hidden sm:block"
                            style={{ color: scrolled ? '#1A1A1A' : '#FFFFFF' }}
                            onClick={() => navigate('/template4/profile')}
                        />
                        <div
                            className="relative cursor-pointer"
                            onClick={() => navigate('/template4/cart')}
                        >
                            <ShoppingCart
                                size={20}
                                className="transition-colors duration-300"
                                style={{ color: scrolled ? '#1A1A1A' : '#FFFFFF' }}
                            />
                            {cartCount > 0 && (
                                <span className="absolute -top-2 -right-2 w-4.5 h-4.5 bg-[#1B4965] text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                                    style={{ width: '18px', height: '18px' }}
                                >
                                    {cartCount}
                                </span>
                            )}
                        </div>

                        {/* Mobile Hamburger */}
                        <button
                            className="md:hidden bg-transparent border-none cursor-pointer p-0"
                            onClick={() => setMobileMenuOpen(true)}
                        >
                            <Menu size={24} style={{ color: scrolled ? '#1B4965' : '#FFFFFF' }} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Full-Screen Overlay */}
            <div
                className="fixed inset-0 z-[100] flex flex-col items-center justify-center transition-opacity duration-300"
                style={{
                    backgroundColor: '#FFFFFF',
                    opacity: mobileMenuOpen ? 1 : 0,
                    pointerEvents: mobileMenuOpen ? 'auto' : 'none',
                }}
            >
                <button
                    onClick={() => setMobileMenuOpen(false)}
                    className="absolute top-5 right-5 bg-transparent border-none cursor-pointer p-0"
                >
                    <X size={28} style={{ color: '#1B4965' }} />
                </button>

                <nav className="flex flex-col items-center gap-8">
                    {navLinks.map((link) => (
                        <button
                            key={link.label}
                            onClick={() => handleNavClick(link.path)}
                            className="text-2xl font-semibold tracking-wider uppercase bg-transparent border-none cursor-pointer text-[#1B4965] hover:text-[#62B6CB] transition-colors duration-300"
                            style={{ fontFamily: "'Work Sans', sans-serif" }}
                        >
                            {link.label}
                        </button>
                    ))}
                </nav>
            </div>
        </>
    );
};

export default Header4;
